import { type MemoExoticComponent, memo, type ReactElement } from 'react';

import { STICKER_SIZES, STICKER_SIZES_MAP, STICKER_TYPES } from './constants';
import { BookStickerIllustration } from './illustrations/BookStickerIllustration';
import { BrainStickerIllustration } from './illustrations/BrainStickerIllustration';
import { CardStickerIllustration } from './illustrations/CardStickerIllustration';
import { CheckListStickerIllustration } from './illustrations/CheckListStickerIllustration';
import { ClassJoinedStickerIllustration } from './illustrations/ClassJoinedStickerIllustration';
import { DocumentsStickerIllustration } from './illustrations/DocumentsStickerIllustration';
import { EggStickerIllustration } from './illustrations/EggStickerIllustration';
import { FireStickerIllustration } from './illustrations/FireStickerIllustration';
import { HandsHeartStickerIllustration } from './illustrations/HandsHeartStickerIllustration';
import { HandshakeStickerIllustration } from './illustrations/HandshakeStickerIllustration';
import { K7StickerIllustration } from './illustrations/K7StickerIllustration';
import { LockStickerIllustration } from './illustrations/LockStickerIllustration';
import { MemoStickerIllustration } from './illustrations/MemoStickerIllustration';
import { MountainStickerIllustration } from './illustrations/MountainStickerIllustration';
import { PhoneStickerIllustration } from './illustrations/PhoneStickerIllustration';
import { PicturesStickerIllustration } from './illustrations/PicturesStickerIllustration';
import { RatioStickerIllustration } from './illustrations/RatioStickerIllustration';
import { RecStickerIllustration } from './illustrations/RecStickerIllustration';
import { RobotStickerIllustration } from './illustrations/RobotStickerIllustration';
import { SnapFingersStickerIllustration } from './illustrations/SnapFingersStickerIllustration';
import { TestStickerIllustration } from './illustrations/TestStickerIllustration';
import { ThumbStickerIllustration } from './illustrations/ThumbStickerIllustration';
import { TrainingStickerIllustration } from './illustrations/TrainingStickerIllustration';
import { TrophiesStickerIllustration } from './illustrations/TrophiesStickerIllustration';
import { TrophyStickerIllustration } from './illustrations/TrophyStickerIllustration';

type StickerProps = {
  type: STICKER_TYPES;
  size?: STICKER_SIZES;
};

export type StickerIllustrationProps = { size: number };

const STICKER_TYPES_MAP = (
  size: StickerIllustrationProps['size'],
): Record<STICKER_TYPES, ReactElement> => ({
  [STICKER_TYPES.FIRE]: <FireStickerIllustration size={size} />,
  [STICKER_TYPES.BOOK]: <BookStickerIllustration size={size} />,
  [STICKER_TYPES.BRAIN]: <BrainStickerIllustration size={size} />,
  [STICKER_TYPES.CARD]: <CardStickerIllustration size={size} />,
  [STICKER_TYPES.CHECK_LIST]: <CheckListStickerIllustration size={size} />,
  [STICKER_TYPES.DOCUMENTS]: <DocumentsStickerIllustration size={size} />,
  [STICKER_TYPES.EGG]: <EggStickerIllustration size={size} />,
  [STICKER_TYPES.HANDSHAKE]: <HandshakeStickerIllustration size={size} />,
  [STICKER_TYPES.HANDS_HEART]: <HandsHeartStickerIllustration size={size} />,
  [STICKER_TYPES.K7]: <K7StickerIllustration size={size} />,
  [STICKER_TYPES.LOCK]: <LockStickerIllustration size={size} />,
  [STICKER_TYPES.MEMO]: <MemoStickerIllustration size={size} />,
  [STICKER_TYPES.MOUNTAIN]: <MountainStickerIllustration size={size} />,
  [STICKER_TYPES.PHONE]: <PhoneStickerIllustration size={size} />,
  [STICKER_TYPES.PICTURES]: <PicturesStickerIllustration size={size} />,
  [STICKER_TYPES.RATIO]: <RatioStickerIllustration size={size} />,
  [STICKER_TYPES.REC]: <RecStickerIllustration size={size} />,
  [STICKER_TYPES.ROBOT]: <RobotStickerIllustration size={size} />,
  [STICKER_TYPES.SNAP_FINGERS]: <SnapFingersStickerIllustration size={size} />,
  [STICKER_TYPES.TEST]: <TestStickerIllustration size={size} />,
  [STICKER_TYPES.THUMB]: <ThumbStickerIllustration size={size} />,
  [STICKER_TYPES.TROPHIES]: <TrophiesStickerIllustration size={size} />,
  [STICKER_TYPES.TROPHY]: <TrophyStickerIllustration size={size} />,
  [STICKER_TYPES.TRAINING]: <TrainingStickerIllustration size={size} />,
  [STICKER_TYPES.CLASS_JOINED]: <ClassJoinedStickerIllustration size={size} />,
});

const StickerBase = ({ type, size = STICKER_SIZES.SMALL }: StickerProps) => {
  return STICKER_TYPES_MAP(STICKER_SIZES_MAP[size])[type];
};

type CompoundedComponent = MemoExoticComponent<typeof StickerBase> & {
  SIZES: typeof STICKER_SIZES;
  TYPES: typeof STICKER_TYPES;
};

const Sticker = memo(StickerBase) as CompoundedComponent;

Sticker.SIZES = STICKER_SIZES;
Sticker.TYPES = STICKER_TYPES;

export { Sticker };
